/**
 * AuditorSeo — el auditor SEO público de la landing.
 *
 * Un visitante anónimo busca su negocio en Google, elige entre cuatro, y ve un
 * análisis comprimido de su ficha con un bloque final bloqueado que lleva al
 * alta.
 *
 * Tres cosas que hay que saber antes de tocarlo:
 *
 * 1. **El `sid` no es opcional.** Sale de `/search` y es lo que autoriza a
 *    pedir `/report` de uno de los locales que esa búsqueda ofreció. Sin él, el
 *    backend sería un proxy gratuito de Google Place Details (ver
 *    `QR/api/public_audit_views.py`). Si se pierde por el camino, todo responde
 *    `session_expired` y parece un bug del backend.
 * 2. **El CTA manda a registrarse con UN local**, no con el valor del
 *    deslizador de la tabla de precios. El visitante acaba de encontrar un
 *    negocio; mandarlo a un checkout de siete locales porque antes movió un
 *    slider es la forma más rápida de perderlo.
 * 3. **El componente no sabe dónde está montado.** Ni ruta, ni nav, ni footer:
 *    hoy vive en una página de prueba y mañana en una sección de la landing.
 * 4. **Se esconde solo si la función está apagada.** Lo pregunta al backend
 *    (`/public/audit/status`) antes de pintar nada. Sin eso, montarlo en la
 *    landing con `PUBLIC_AUDIT_ENABLED=False` le daría al visitante una caja de
 *    búsqueda que responde errores, que es peor que no enseñar nada. Y ante
 *    cualquier duda —error de red, respuesta rara— no se pinta: para algo que
 *    cuesta dinero, la dirección segura es callarse.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useLanguage } from '@/components/LanguageContext';
import { pickAuditorContent } from '@/pages/landingAuditor.content';
import { withCta } from '@/lib/ctaTracking';
import {
  auditErrorCode,
  getAuditReport,
  getAuditStatus,
  searchPlaces,
} from '@/api/publicAuditService';

import AuditorLocked from './AuditorLocked';
import AuditorReport from './AuditorReport';
import AuditorSearch from './AuditorSearch';

export default function AuditorSeo({ conCabecera = true, className = 'max-w-3xl mx-auto px-6' }) {
  const { language, t } = useLanguage();
  const c = pickAuditorContent(language);
  const navigate = useNavigate();

  const [results, setResults] = useState(null);
  const [sid, setSid] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [report, setReport] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  // `null` = todavía no se sabe. Mientras tanto no se pinta nada: es preferible
  // que la sección aparezca un instante después a que aparezca y desaparezca.
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    let vivo = true;
    getAuditStatus().then((ok) => { if (vivo) setEnabled(ok); });
    return () => { vivo = false; };
  }, []);

  const handleSearch = useCallback(async ({ query, near }) => {
    setSearching(true);
    setErrorCode(null);
    setReport(null);
    try {
      const data = await searchPlaces({ query, near, language });
      setResults(data.results);
      setSid(data.sid);
    } catch (error) {
      setResults([]);
      setSid(null);
      setErrorCode(auditErrorCode(error));
    } finally {
      setSearching(false);
    }
    // `language` es una cadena, así que la identidad de este callback solo
    // cambia cuando cambia de verdad el idioma. El debounce de AuditorSearch
    // depende de eso.
  }, [language]);

  const handlePick = useCallback(async (result) => {
    setLoadingReport(true);
    setErrorCode(null);
    try {
      const data = await getAuditReport({ sid, placeId: result.placeId });
      setReport(data);
    } catch (error) {
      setErrorCode(auditErrorCode(error));
    } finally {
      setLoadingReport(false);
    }
  }, [sid]);

  // Mismo viaje que el botón de «empezar prueba» de la landing: registro
  // recordando el plan y, al volver autenticado a /Pricing?autocheckout=1,
  // Stripe se dispara solo. `withCta` deja que una campaña con `?cta=` gane a
  // este botón, y si no la hay, el registro se lo atribuye él (§124).
  const startTrial = useCallback((ctaId) => {
    const target = '/pricing?autocheckout=1&seats=1&period=monthly';
    navigate(withCta(`/register?redirect=${encodeURIComponent(target)}`, ctaId));
  }, [navigate]);

  const errorText = errorCode
    ? (c.errors[errorCode] || c.errors.generic)
    : null;

  if (!enabled) return null;

  return (
    <section className={className}>
      {/* La cabecera es opcional desde que el auditor se monta en dos sitios:
          en `/auditor`, donde es la página entera y necesita presentarse, y
          dentro de la caja ámbar de la home V3, que YA trae su propio titular
          («Cada una de tus fichas ya tiene su puntuación») y su microcopy. Con
          las dos salían dos encabezados seguidos diciendo lo mismo. */}
      {conCabecera ? (
        <>
          <p className="kicker text-[#feae2c] font-semibold text-sm">{c.kicker}</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-[-0.03em] text-ink">
            {c.title}
          </h2>
          <p className="mt-3 text-lg text-slate-500">{c.subtitle}</p>
        </>
      ) : null}

      <div className={conCabecera ? 'mt-8' : ''}>
        <AuditorSearch
          c={c}
          results={results}
          searching={searching}
          onSearch={handleSearch}
          onPick={handlePick}
        />
      </div>

      {errorText && (
        <p className="mt-4 px-2 text-sm text-slate-600" role="status">{errorText}</p>
      )}

      {loadingReport && (
        <p className="mt-6 px-2 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {c.report.loading}
        </p>
      )}

      {report && !loadingReport && (
        <>
          <div className="mt-8">
            <AuditorReport c={c} report={report} t={t} />
          </div>
          <div className="mt-6">
            {/* El id va LITERAL y no por una constante: la guarda de CI
                (`src/lib/__tests__/ctaRegistry.test.js`) barre el fuente con un
                regex, y con una variable la entrada del registro parecería
                muerta y el test fallaría. */}
            <AuditorLocked
              c={c}
              ctaId="landing.auditor.desbloquear"
              onUpgrade={startTrial}
            />
          </div>
        </>
      )}
    </section>
  );
}
